import {
  Box,
  Flex,
  Grid,
  Heading,
  mdBumps,
  OptionTile,
  xsBumps,
} from '@wrisk/ui-components'
import { uniqBy } from 'lodash-es'
import React, { FunctionComponent, useCallback, useState } from 'react'

import { getCurrentQuote, Proposal } from '../../../../domain'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { isPaymentScheduleType } from '../../../../state/configuration'
import { usePaymentSchedule } from '../../../product/paymentScheduleContext'
import { useProposal } from '../../../product/proposal/ProposalProvider'

export interface PaymentOptionsProps {
  tKey: TKeyBuilder
}

const key = 'payment-frequency'

const uniqAvailablePaymentScheduleTypes = (proposal: Proposal) =>
  uniqBy(
    getCurrentQuote(proposal).availableCoverLevelOptions,
    (it) => it.paymentScheduleType,
  ).map((it) => it.paymentScheduleType)

export const shouldRenderPaymentFrequency = (proposal: Proposal) =>
  uniqAvailablePaymentScheduleTypes(proposal).length > 1

export const PaymentFrequency: FunctionComponent<PaymentOptionsProps> = ({ tKey }) => {
  const { t } = useWriskTranslation()
  const { proposal } = useProposal()
  const { paymentScheduleType, setPaymentScheduleType } = usePaymentSchedule()

  const header = t(tKey(key, 'header'), { defaultValue: null })
  const options = uniqAvailablePaymentScheduleTypes(proposal)

  const [activeTile, setActiveTile] = useState(paymentScheduleType?.toString())

  const onSelect = useCallback(
    (value: string) => {
      setActiveTile(value)
      if (isPaymentScheduleType(value)) {
        setPaymentScheduleType(value)
      } else {
        throw new Error(`No payment schedule of type ${value}`)
      }
    },
    [setActiveTile, setPaymentScheduleType],
  )

  if (!shouldRenderPaymentFrequency(proposal)) return null

  return (
    <Flex flexDirection='column' alignItems='flex-start' width={1} gap={mdBumps}>
      {header && (
        <Box>
          <Heading variant='h4' as='h4' typoSize='md'>
            {header}
          </Heading>
        </Box>
      )}
      <Grid
        gridTemplateColumns={['repeat(1, 1fr)', 'repeat(2, 1fr)']}
        width={1}
        gap={xsBumps}
      >
        {options.map((it) => (
          <OptionTile
            key={it}
            name={key}
            value={it}
            text={t(tKey(key, it))}
            onSelect={onSelect}
            checked={activeTile === it}
          />
        ))}
      </Grid>
    </Flex>
  )
}
